import { Component, OnInit, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideCircleAlert } from '@ng-icons/lucide';
import { filter, map, shareReplay, startWith, Subject, take } from 'rxjs';

import { getLogoutRedirectTarget } from '@app/core/auth/logout-redirect.util';
import { TopbarFilterComponent } from '@app/core/layouts/components/topbar-filter/topbar-filter.component';
import { AppContextService } from '@app/core/services/app-context.service';
import { AuthSessionService } from '@app/core/services/auth-session.service';
import { BreadcrumbService } from '@app/core/services/breadcrumb.service';
import { ConfirmationModalService } from '@app/core/services/confirmation-modal.service';
import { DayjsService, Inclusivity } from '@app/core/services/dayjs.service';
import { StorageService } from '@app/core/services/storage.service';
import { AvatarComponent } from '@app/core/ui/components/avatar/avatar.component';
import { DrawerService } from '@app/core/ui/components/drawer';
import {
  DropdownItem,
  DropdownComponent
} from '@app/core/ui/components/dropdown/dropdown.component';
import { ModalService } from '@app/core/ui/components/modal';
import { LoadingStateEnum } from '@app/core/ui/enums';
import { DeadlineContextService } from '@app/features/home/components/deadline-calendar/service/deadline-context.service';
import {
  LastDaysReminderModalComponent,
  LastDaysReminderModel,
  StorageLastDaysReminder
} from '@app/features/home/components/last-days-reminder-modal/last-days-reminder-modal.component';
import { DeadlineDatesModel } from '@app/features/home/models/deadline.model';
import { DeadlineNameEnum } from '@app/shared/enums/deadline.enum';
import { StorageEnum } from '@app/shared/enums/storage.enum';

import { environment } from '@env/environment';

import { BreadcrumbComponent } from '../components/breadcrumb/breadcrumb.component';
import { NavigationMenuComponent } from '../components/navigation-menu/navigation-menu.component';
import { PendingIssuesNotificationComponent } from './components/pending-issues-notification/pending-issues-notification.component';

@Component({
  selector: 'mw-main-layout',
  standalone: true,
  imports: [
    RouterModule,
    NavigationMenuComponent,
    TopbarFilterComponent,
    DropdownComponent,
    AvatarComponent,
    BreadcrumbComponent,
    NgIconComponent
  ],
  providers: [
    provideIcons({
      lucideCircleAlert
    })
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss'
})
export class MainLayoutComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly breadcrumb = inject(BreadcrumbService);
  private readonly auth = inject(AuthSessionService);
  private readonly appCtx = inject(AppContextService);
  private readonly drawer = inject(DrawerService);
  private readonly modal = inject(ModalService);
  private readonly storage = inject(StorageService);
  private readonly deadlineCtx = inject(DeadlineContextService);
  private readonly dayJS = inject(DayjsService);
  private readonly confirmationModal = inject(ConfirmationModalService);

  private readonly isPrivilegedAccess = toSignal(this.auth.isPrivilegedAccess$);
  protected readonly username = toSignal(
    this.auth.session$.pipe(map((session) => session?.user?.name ?? ''))
  );

  protected readonly isSettingsRoute = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.isSettingsUrl(this.router.url)),
      startWith(this.isSettingsUrl(this.router.url))
    ),
    { initialValue: this.isSettingsUrl(this.router.url) }
  );

  protected readonly pendingIssues = toSignal(this.appCtx.pendingIssues$);

  protected readonly breadcrumbs = toSignal(
    this.breadcrumb.getBreadcrumbs$().pipe(shareReplay(1)),
    {
      initialValue: []
    }
  );

  protected readonly profileMenu = computed<DropdownItem[]>(() => [
    {
      name: 'Painel de gerenciamento',
      action: (): void => {
        this.router.navigate(['configuracoes']);
      },
      hidden: !this.isPrivilegedAccess()
    },
    {
      name: 'Dados da escola',
      action: (): void => {
        this.router.navigate(['configuracoes-gerais']);
      }
    },
    {
      name: 'Sair',
      action: (): void => this.handleLogout()
    },
    {
      name: `Versão ${environment.version}`,
      static: true
    }
  ]);

  private readonly lastDaysReminderTrigger = new Subject<{
    cycleId: number;
    deadlines: DeadlineDatesModel[];
  }>();

  ngOnInit(): void {
    this.initializeAppContext();
    this.subscribeToCycleChange();
    this.confirmationModal.startWatchingRoutes();
    this.confirmationModal.otherModalsAllowed$
      .pipe(take(1))
      .subscribe(() => this.listenLastDayReminderTrigger());
  }

  private initializeAppContext(): void {
    this.appCtx.syncStorage();
  }

  private subscribeToCycleChange(): void {
    this.appCtx.onSelectedCycleChange().subscribe((cycleId) => {
      this.deadlineCtx.loadDeadlines().subscribe((deadlines) => {
        this.lastDaysReminderTrigger.next({
          cycleId,
          deadlines
        });
      });
    });
  }

  private listenLastDayReminderTrigger() {
    this.lastDaysReminderTrigger
      .asObservable()
      .pipe(
        map(({ cycleId, deadlines }) => {
          const date = deadlines?.find(
            (deadline) => deadline.name === DeadlineNameEnum.PlatformFeeding
          )?.date;
          return {
            cycleId,
            date
          };
        }),
        filter(({ date }) => !!date)
      )
      .subscribe(({ cycleId, date }) => {
        this.handleDateComparisonAndModal(cycleId, date);
      });
  }

  handleDateComparisonAndModal(cycleId: number, date: string) {
    const today = this.dayJS.dateNow();
    const limit = this.dayJS.convertToDate(date);
    const oneMonthBeforeLimit = this.dayJS.subtractMonths(1, limit);
    const dateIsBetweenTimeInterval = this.dayJS.compareIsBetween(
      today,
      oneMonthBeforeLimit,
      limit,
      'day',
      Inclusivity.includeBoundaries
    );

    if (dateIsBetweenTimeInterval) {
      const storageLimiDate = this.storage.get<StorageLastDaysReminder>(
        StorageEnum.LastDaysReminder,
        'session'
      );

      if (!storageLimiDate?.[cycleId]) {
        this.handleLastDaysReminderModal(cycleId, date);
      }
    }
  }

  handleLastDaysReminderModal(cycleId: number, limitDate: string) {
    this.modal.open<LastDaysReminderModel>(LastDaysReminderModalComponent, {
      data: { limitDate, cycleId },
      options: {
        size: 'sm',
        closeButton: false,
        disableBackdropClose: true
      }
    });
  }

  handleLogout(): void {
    this.auth.removeSession({
      redirectTo: getLogoutRedirectTarget()
    });
  }

  handlePendingIssuesNotification(): void {
    if (this.pendingIssues().status === LoadingStateEnum.loading) {
      return;
    }

    const areAllBooksFinished = this.pendingIssues()?.result?.every(
      (pendingIssues) => !!pendingIssues.isFinished
    );

    const description =
      this.pendingIssues().result.length >= 0 || !areAllBooksFinished
        ? 'Os livros irão para produção após a conclusão das pendências.'
        : 'Não há pendências.';

    this.drawer.open(PendingIssuesNotificationComponent, {
      title: 'Pendências',
      description
    });
  }

  handleCleanLastDayReminderSession() {
    if (this.storage.get(StorageEnum.LastDaysReminder, 'session')) {
      this.storage.delete(StorageEnum.LastDaysReminder, 'session');
    }
  }

  private isSettingsUrl(url: string): boolean {
    return /\/configuracoes(-gerais)?(\/|$)/.test(url);
  }
}
